/**
 * Hairstyle API Helpers (Gemini Gateway)
 * 
 * Consolidates hairstyle API calls behind the current Gemini gateway routes:
 * - POST /generate-hairstyle (Node proxy → Python → Gemini)
 * - POST /shape-prompts (Node proxy → Python → Gemini)
 */

const AI_HAIRSTYLE_CONFIG = {
  api: {
    baseUrl: process.env.REACT_APP_HAIRSTYLE_API_BASE || '', // Empty = same origin
  }
};

/**
 * Validate image file before upload
 * Returns { isValid: boolean, errors: string[] } for compatibility with existing code
 */
export const validateImageFile = (file) => {
  const errors = [];
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  
  if (!file) {
    errors.push('No file provided');
    return { isValid: false, errors };
  }
  
  if (!validTypes.includes(file.type)) {
    errors.push('Please upload a JPEG or PNG image');
  }
  
  if (file.size > 5 * 1024 * 1024) {
    errors.push('Image size must be under 5MB');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Handle API errors
 */
export const handleHairstyleError = (error) => {
  if (error.response) {
    const data = error.response.data;
    if (data.code === 'INSUFFICIENT_CREDITS') {
      // Check if this is a barber-specific message
      if (data.message && data.message.includes('contact your salon admin')) {
        return data.message; // Use the barber-specific message from backend
      }
      return 'Insufficient credits. Please purchase more credits to continue.';
    }
    if (data.code === 'SUBSCRIPTION_INACTIVE') {
      return 'Your subscription is not active. Please check your subscription status.';
    }
    if (data.code === 'NO_CREDITS_ALLOCATED') {
      return data.message || "You don't have any allocated credits. Please contact your salon admin.";
    }
    return data.message || data.error || 'AI generation failed';
  }
  if (error.code === 'ECONNREFUSED') {
    return 'AI service is currently unavailable. Please try again later.';
  }
  return error.message || 'An unexpected error occurred';
};

/**
 * API Helpers
 */
export const hairstyleApiHelpers = {
  /**
   * POST /shape-prompts
   * Analyze face shape (no credits deducted)
   */
  async postShapePrompts(file) {
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      throw new Error(validation.errors[0]);
    }

    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('token');
    const headers = {
      ...(token && { Authorization: `Bearer ${token}` })
    };

    const response = await fetch(`${AI_HAIRSTYLE_CONFIG.api.baseUrl}/shape-prompts`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.message || 'Shape analysis failed');
      error.response = { status: response.status, data: errorData };
      throw error;
    }

    return response.json();
  },

  /**
   * POST /face-analysis
   * Face Analysis only (deducts 1 credit on success)
   * Returns face shape + measurements + visible hairProfile without generation
   */
  async postFaceAnalysis(file) {
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      throw new Error(validation.errors[0]);
    }

    const formData = new FormData();
    formData.append('file', file);
    // Per-click id for server-side idempotent billing (same pattern as generation flow).
    formData.append('request_id', crypto.randomUUID());

    const token = localStorage.getItem('token');
    const headers = {
      ...(token && { Authorization: `Bearer ${token}` })
    };

    const response = await fetch(`${AI_HAIRSTYLE_CONFIG.api.baseUrl}/face-analysis`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.message || 'Face analysis failed');
      error.response = { status: response.status, data: errorData };
      throw error;
    }

    return response.json();
  },

  /**
   * POST /generate-hairstyle
   * Generate hairstyle (deducts 1 credit on success)
   */
  async postGenerateHairstyle({
    file,
    custom_hint,
    styleIndex,
    force_shape,
    scan_id,
    style,
    faceShape,
    hairProfile,
    haircutStructure,
    generationMode,
    selectedSuggestionName,
    selectedSuggestionDefinition,
    resolvedStyleName,
    resolvedStyleDefinition,
    presetId,
    presetName,
    presetCategory,
    presetLock,
  }) {
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      throw new Error(validation.errors[0]);
    }

    const formData = new FormData();
    formData.append('file', file);
    if (custom_hint) formData.append('custom_hint', custom_hint);
    if (styleIndex !== undefined) formData.append('styleIndex', styleIndex);
    if (force_shape) formData.append('force_shape', force_shape);
    if (scan_id) formData.append('scan_id', scan_id);
    if (style) formData.append('style', style);
    if (faceShape) formData.append('faceShape', faceShape);
    if (hairProfile && typeof hairProfile === 'object') {
      formData.append('hairProfile', JSON.stringify(hairProfile));
    }
    if (haircutStructure && typeof haircutStructure === 'object') {
      formData.append('haircutStructure', JSON.stringify(haircutStructure));
    }
    if (generationMode) formData.append('generationMode', generationMode);
    if (selectedSuggestionName) formData.append('selectedSuggestionName', selectedSuggestionName);
    if (selectedSuggestionDefinition) formData.append('selectedSuggestionDefinition', selectedSuggestionDefinition);
    if (resolvedStyleName) formData.append('resolvedStyleName', resolvedStyleName);
    if (resolvedStyleDefinition) formData.append('resolvedStyleDefinition', resolvedStyleDefinition);
    if (presetId) formData.append('preset_id', presetId);
    if (presetName) formData.append('preset_name', presetName);
    if (presetCategory) formData.append('preset_category', presetCategory);
    if (presetLock && typeof presetLock === 'object') {
      formData.append('preset_lock', JSON.stringify(presetLock));
    }
    // Unique per-click ID so the backend charges exactly once per intentional generation
    // while still deduplicating true HTTP-level retries of the same request.
    formData.append('request_id', crypto.randomUUID());

    const token = localStorage.getItem('token');
    const headers = {
      ...(token && { Authorization: `Bearer ${token}` })
    };

    const response = await fetch(`${AI_HAIRSTYLE_CONFIG.api.baseUrl}/generate-hairstyle`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.message || 'Generation failed');
      error.response = { status: response.status, data: errorData };
      throw error;
    }

    return response.json();
  }
};

